import React from 'react';
import { C } from '../../theme.js';
import { Button, Eyebrow, InfoIcon } from '../../components/Primitives.jsx';
import { pipelineOrder } from '../../data.js';
import { Segmented } from '../InspectorPrimitives.jsx';
import { ConditionRows } from './ConditionRows.jsx';
import { ExpressionEditorModal } from '../transform/ExpressionEditorModal.jsx';
import { resolveEditorRows } from '../transform/codeMode/rowSource.js';
import { firstLines } from '../transform/codeMode/blockCode.js';
import {
  builderGate, gateCodePatch, gateSummary, gateToggleWrite, gateWantsHalt, gateWithConditions,
  gateWithStopOnFalse, hasSavedGateCode, isCodeGate, needsGateReview,
} from '../transform/codeMode/gateCode.js';

// ── ApplyWhenSection ─────────────────────────────────────────────────────
// The shared, optional "Apply only when" gate for row-wise blocks (engine:
// server/app/engine/row_gate.py). Stored on the step config as
//   apply_when: { conditions: [...], conjunction: 'AND' | 'OR' }          (Builder)
//   apply_when: { mode: 'code', expr: {v, lang, src, reviewed}, ... }     (Code)
// A row where the gate is true is processed as always; a row where it is
// false passes through untouched. In Code mode the expression is ONE
// true/false condition (it returns nothing else): the same editor window as
// Case When, with the columns on the left and the helpers/snippets on the
// right. The key exists only while it has at least one condition or saved
// code — emptying the builder list removes it, so saved configs stay clean
// and "absent" keeps meaning "every row". Rendered once by StepInspector for
// the step types in GATEABLE_STEPS (data.js); no individual inspector knows
// about it. Sits at the TOP of the config panel (it is evaluated first).
// `columns` must be the step's INPUT columns (the gate is evaluated on the
// row as it arrives, not on this step's outputs).
export function ApplyWhenSection({ step, onChange, columns, secondaryDatasets, sheets, pipeline, runMap, onBrowseJoinFile, uploadedRows }) {
  const c = step.config || {};
  const gate = c.apply_when;
  const conditions = Array.isArray(gate?.conditions) ? gate.conditions : [];

  // Choosing "Code" with nothing saved stays local (`codeDraft`) and opens
  // the editor; `mode:"code"` and the source are written together, only by
  // Apply. Closing the first editor without Apply writes nothing.
  const [codeDraft, setCodeDraft] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  // Collapsed by default — this section is optional and sits at the top of
  // every gateable step's config. Starts (and resets, per step) open when
  // the step already has a gate, so an existing condition is never hidden
  // on open; a step with no gate starts collapsed to save space.
  const hasGate = conditions.length > 0 || isCodeGate(gate);
  const [open, setOpen] = React.useState(hasGate);
  React.useEffect(() => {
    setCodeDraft(false);
    setEditing(false);
    setOpen(hasGate);
    // Only re-derive `open` when the SELECTED STEP changes — not every time
    // `hasGate` itself changes, which would snap the section back open the
    // instant the user types the step's first condition while it's open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);
  const codeView = isCodeGate(gate) || codeDraft;

  const setGate = (next) => {
    const { apply_when: _drop, ...rest } = c;
    onChange({ ...step, config: next ? { ...rest, apply_when: next } : rest });
  };
  const write = (nextConds, conjunction) => setGate(gateWithConditions(gate, nextConds, conjunction));
  const addCond = () => write([...conditions, { column: columns?.[0]?.name || '', op: 'eq', value: '' }]);
  const updateCond = (i, patch) => write(conditions.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const removeCond = (i) => write(conditions.filter((_, k) => k !== i));

  const setStopOnFalse = (v) => setGate(gateWithStopOnFalse(gate, v));

  const chooseMode = (next) => {
    if (next === 'code') {
      const w = gateToggleWrite(gate);
      if (w) setGate(w);
      else { setCodeDraft(true); setEditing(true); }
    } else {
      setCodeDraft(false);
      if (isCodeGate(gate)) setGate(builderGate(gate));
    }
  };

  const sourceCtx = {
    secondaryDatasets, sheets, pipeline, currentStep: step, runMap,
    onBrowse: onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined,
    buildTargetName: pipeline?.inputFile?.buildTargetName,
  };

  // The gate reads this step's own input, so the preview rows are the
  // previous step's rows (or the uploaded data for the first step).
  const editorRows = React.useMemo(() => resolveEditorRows({
    source: undefined, orderedSteps: pipelineOrder(pipeline?.steps || []), stepId: step.id,
    runMap, sheets, secondaryDatasets, uploadedRows, steps: pipeline?.steps,
  }), [pipeline?.steps, step.id, runMap, sheets, secondaryDatasets, uploadedRows]);

  return (
    <div style={{
      marginBottom: 18, padding: '12px 14px', background: C.input,
      border: `1px solid ${C.border}`, borderRadius: 10,
    }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 6px',
          cursor: 'pointer', outline: 'none', userSelect: 'none',
        }}
      >
        <span style={{
          display: 'inline-block', color: C.fg5, fontSize: '.7rem', flexShrink: 0,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s',
        }}>›</span>
        <span style={{ color: C.fg2, fontSize: '.78rem', fontWeight: 600 }}>Apply only when (optional)</span>
        <span onClick={(e) => e.stopPropagation()}>
          <InfoIcon text="Limit this step to rows that meet a condition. Rows that don't are passed through completely unchanged (nothing added, removed, or modified). Leave empty to apply to every row." />
        </span>
        {!open && (
          <span style={{ marginLeft: 'auto', color: C.fg5, fontSize: '.72rem', fontWeight: 500, flexShrink: 0 }}>
            {gateSummary(gate)}
          </span>
        )}
      </div>

      {open && (<>
      <div style={{ marginBottom: 8 }}>
        <Segmented
          value={codeView ? 'code' : 'builder'}
          onChange={chooseMode}
          options={[{ value: 'builder', label: 'Builder' }, { value: 'code', label: 'Code' }]}
        />
      </div>

      {codeView ? (
        <div style={{ background: C.app, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Eyebrow style={{ marginBottom: 0 }}>Condition</Eyebrow>
            <div style={{ flex: 1 }} />
            <Button kind="accent" size="sm" onClick={() => setEditing(true)}>{hasSavedGateCode(gate) ? 'Edit code' : 'Write code'}</Button>
          </div>
          {needsGateReview(gate) && (
            <div style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginBottom: 8 }}>
              Imported code: open the editor and Apply to enable it
            </div>
          )}
          {hasSavedGateCode(gate) ? (() => {
            const fl = firstLines(gate.expr.src, 8);
            return (
              <pre title="Edit code to change it" style={{
                margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '.76rem', color: C.fg2,
                background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px',
                overflowX: 'auto', whiteSpace: 'pre',
              }}>{fl.text}{fl.more ? `\n… ${fl.more} more line${fl.more === 1 ? '' : 's'}` : ''}</pre>
            );
          })() : (
            <div style={{ color: C.fg5, fontSize: '.78rem' }}>No code yet. Write one expression that is true or false for each row.</div>
          )}
          <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 8 }}>
            The step runs for rows where this is true; other rows pass through unchanged. The dropdown conditions are kept but not used while this is in Code mode.
          </div>
        </div>
      ) : (<>
        {conditions.length === 0 && (
          <div style={{ color: C.fg5, fontSize: '.74rem', marginBottom: 8 }}>Applies to every row.</div>
        )}
        {conditions.length > 1 && (
          <div style={{ marginBottom: 8 }}>
            <Eyebrow style={{ marginBottom: 6 }}>Match</Eyebrow>
            <Segmented
              value={gate?.conjunction || 'AND'}
              onChange={(v) => write(conditions, v)}
              options={[{ value: 'AND', label: 'ALL · AND' }, { value: 'OR', label: 'ANY · OR' }]}
            />
          </div>
        )}
        <ConditionRows
          conditions={conditions}
          columns={columns}
          onUpdate={updateCond}
          onRemove={removeCond}
          variant="plain"
          sourceCtx={sourceCtx}
        />
        <Button kind="ghost" size="sm" onClick={addCond} icon="＋">Add condition</Button>
      </>)}

      <label
        title="When checked, a row that fails this condition halts the whole pipeline run immediately — no further steps run — instead of passing through unchanged."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          color: C.fg4, fontSize: '.78rem', marginTop: 10, paddingTop: 10,
          borderTop: `1px solid ${C.border}`, width: '100%',
        }}
      >
        <input
          type="checkbox"
          checked={gateWantsHalt(gate)}
          onChange={(e) => setStopOnFalse(e.target.checked)}
          style={{ accentColor: C.accent }}
        />
        Stop the pipeline on a False result (instead of skipping this step)
      </label>
      </>)}

      {editing && (
        <ExpressionEditorModal
          kind="condition"
          title="Condition editor"
          caseLabel="Apply only when"
          hint="Write one expression that is true or false for each row. The step runs for rows where it is true; other rows pass through unchanged. There is nothing to return except true or false."
          initialSrc={gate?.expr?.src || ''}
          columns={columns || []}
          rows={editorRows.rows}
          rowsOrigin={editorRows.origin}
          rowsNote={editorRows.note}
          pipelineId={pipeline?.id}
          onClose={() => { setEditing(false); if (!hasSavedGateCode(gate)) setCodeDraft(false); }}
          onApply={(src) => {
            setCodeDraft(false);
            setEditing(false);
            setGate(gateCodePatch(gate, src));
          }}
        />
      )}
    </div>
  );
}
